/*
  The site header (sections/site-header.liquid).

  From 1200px a menu link with a panel opens it when the pointer rests on the link or the link
  takes keyboard focus, and still goes to its page when clicked. On a touch screen the first
  tap opens the panel and the second follows the link. Escape, leaving the link and its panel,
  or a click on the dimmed page closes it.

  Below 1200px the menu button opens the menu under the bar with every accordion closed, so the
  whole menu fits on the screen, and one accordion opens at a time. While it is open the page
  behind stays still, Tab stays within the menu and its button, and Escape closes it.
*/
if (!customElements.get('site-header')) {
  class SiteHeader extends HTMLElement {
    // a pointer passing over the bar should not flash a panel open
    static OPEN_DELAY = 120;
    static CLOSE_DELAY = 150;

    connectedCallback() {
      this.bar = this.querySelector('.site-header__bar');
      this.panelItems = Array.from(this.querySelectorAll('.site-header__item--panel'));
      this.menuButton = this.querySelector('.site-header__menu-button');
      this.menu = this.querySelector('.site-header__menu');
      this.menuToggles = Array.from(this.querySelectorAll('.site-header__menu-toggle'));
      this.desktop = window.matchMedia('(min-width: 1200px)');

      this.openItem = null;
      this.timer = null;
      this.pointerType = 'mouse';
      this.openBeforeTap = false;
      this.reopenOnFocus = true;

      this.panelItems.forEach((item) => this.setUpPanelItem(item));

      this.querySelector('.site-header__scrim')?.addEventListener('click', () => this.closePanel());
      document.addEventListener('click', (event) => {
        if (this.openItem && !this.openItem.contains(event.target)) this.closePanel();
      });

      this.menuButton?.addEventListener('click', () => (this.isMenuOpen() ? this.closeMenu() : this.openMenu()));
      this.menuToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.setAccordion(toggle, toggle.getAttribute('aria-expanded') !== 'true'));
      });

      // the search drawer opens over the menu; close the menu first so the two scroll locks do not cross
      this.querySelector('.site-header__util--search summary')?.addEventListener('click', () => this.closeMenu(), {
        capture: true,
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.desktop.addEventListener('change', () => {
        this.closePanel();
        this.closeMenu();
      });
    }

    setUpPanelItem(item) {
      const link = item.querySelector('.site-header__link');

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.schedule(() => this.openPanel(item), this.openItem ? 0 : SiteHeader.OPEN_DELAY);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.schedule(() => this.closePanel(), SiteHeader.CLOSE_DELAY);
      });

      link.addEventListener('pointerdown', (event) => {
        this.pointerType = event.pointerType;
        this.openBeforeTap = this.openItem === item;
      });
      link.addEventListener('click', (event) => {
        if (this.pointerType !== 'touch' || this.openBeforeTap || !this.desktop.matches) return;
        event.preventDefault();
        this.openPanel(item);
      });

      item.addEventListener('focusin', () => {
        if (this.reopenOnFocus && this.pointerType !== 'touch') this.openPanel(item);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget) && this.openItem === item) this.closePanel();
      });

      // load the tile photos once the pointer or the focus comes near, not with every page
      const loadImages = () => item.querySelectorAll('img[loading="lazy"]').forEach((image) => (image.loading = 'eager'));
      item.addEventListener('pointerenter', loadImages, { once: true });
      item.addEventListener('focusin', loadImages, { once: true });
    }

    schedule(callback, delay) {
      clearTimeout(this.timer);
      this.timer = delay ? setTimeout(callback, delay) : null;
      if (!delay) callback();
    }

    openPanel(item) {
      clearTimeout(this.timer);
      if (!this.desktop.matches || this.openItem === item) return;
      if (this.openItem) this.setItemOpen(this.openItem, false);
      this.setItemOpen(item, true);
      this.openItem = item;
      this.classList.add('is-panel-open');
    }

    closePanel() {
      clearTimeout(this.timer);
      if (!this.openItem) return;
      this.setItemOpen(this.openItem, false);
      this.openItem = null;
      this.classList.remove('is-panel-open');
    }

    setItemOpen(item, open) {
      item.classList.toggle('is-open', open);
      item.querySelector('.site-header__link').setAttribute('aria-expanded', open);
    }

    isMenuOpen() {
      return this.classList.contains('is-menu-open');
    }

    openMenu() {
      this.style.setProperty('--site-header-menu-top', `${Math.round(this.bar.getBoundingClientRect().bottom)}px`);
      this.setAccordion(null, false);
      this.classList.add('is-menu-open');
      this.menuButton.setAttribute('aria-expanded', 'true');
      this.menu.scrollTop = 0;
      window.bodyScrollLock?.disableBodyScroll(this.menu, { reserveScrollBarGap: true });
    }

    closeMenu() {
      if (!this.isMenuOpen()) return;
      this.classList.remove('is-menu-open');
      this.menuButton.setAttribute('aria-expanded', 'false');
      window.bodyScrollLock?.enableBodyScroll(this.menu);
    }

    setAccordion(toggle, open) {
      this.menuToggles.forEach((other) => other.setAttribute('aria-expanded', other === toggle && open));
    }

    onKeydown(event) {
      // a drawer open inside the header (country, search) handles its own keys
      if (event.target.closest('details[open]')) return;

      if (event.key === 'Escape') {
        if (this.openItem) {
          const link = this.openItem.querySelector('.site-header__link');
          this.closePanel();
          this.reopenOnFocus = false;
          link.focus();
          this.reopenOnFocus = true;
        } else if (this.isMenuOpen()) {
          this.closeMenu();
          this.menuButton.focus();
        }
        return;
      }

      if (event.key === 'Tab' && this.isMenuOpen()) this.keepFocusInMenu(event);
    }

    keepFocusInMenu(event) {
      const focusable = [
        this.menuButton,
        ...this.menu.querySelectorAll(
          '.site-header__menu-toggle, .site-header__menu-link, .site-header__menu-panel a, .site-header__menu-account, .site-header__country-button'
        ),
      ].filter((element) => element.getClientRects().length > 0);
      const index = focusable.indexOf(document.activeElement);
      const step = event.shiftKey ? -1 : 1;
      const next = index === -1 ? focusable[0] : focusable[(index + step + focusable.length) % focusable.length];
      event.preventDefault();
      next.focus();
    }
  }

  customElements.define('site-header', SiteHeader);
}
